import * as THREE from 'three';
import type { ExplorationData } from '../labyrinth/ExplorationData';
import { type Labyrinth, TILE_SIZE, Tile } from '../labyrinth/Labyrinth';

// Fog plane sits just above ceiling height so it reads as a cloudy layer
// covering the map when viewed from the ¾-top-down camera.
const FOG_HEIGHT          = TILE_SIZE + 0.5;
const FOG_FADE_SPEED      = 2.5;             // density units/sec (0..1), matches prior fog fade
const NOISE_TEX_SIZE      = 256;
const NOISE_WORLD_SCALE_A = TILE_SIZE * 6;   // large cloud bands
const NOISE_WORLD_SCALE_B = TILE_SIZE * 2.5; // finer wisps

/**
 * Volumetric-style fog-of-war overlay.
 *
 * A single plane covers the entire map at ceiling height. Opacity is driven
 * per-tile by a low-res DataTexture (R channel = fog density, bilinear-filtered
 * so edges are soft). A scrolling two-octave value-noise texture gives the
 * layer a cloudy moving appearance. Unexplored tiles are fully opaque fog,
 * which effectively masks the walls/ceiling/floor of those tiles under the
 * plane; Base and Door tiles are permanently clear.
 */
export class FogMesh3D {
  readonly group = new THREE.Group();

  private readonly width: number;
  private readonly height: number;
  private readonly density: Uint8Array;      // current per-tile density (0..255)
  private readonly densityTex: THREE.DataTexture;
  private readonly noiseTex: THREE.CanvasTexture;
  private readonly material: THREE.ShaderMaterial;
  private readonly mesh: THREE.Mesh;

  constructor(private readonly lab: Labyrinth, exploration: ExplorationData) {
    this.width  = lab.width;
    this.height = lab.height;

    const pixels = this.width * this.height;
    this.density = new Uint8Array(pixels);
    const texData = new Uint8Array(pixels * 4);

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const i = y * this.width + x;
        const d = this.isClearTile(x, y, exploration) ? 0 : 255;
        this.density[i] = d;
        texData[i * 4] = d;
        texData[i * 4 + 3] = 255;
      }
    }

    this.densityTex = new THREE.DataTexture(texData, this.width, this.height, THREE.RGBAFormat);
    this.densityTex.magFilter = THREE.LinearFilter;
    this.densityTex.minFilter = THREE.LinearFilter;
    this.densityTex.wrapS = THREE.ClampToEdgeWrapping;
    this.densityTex.wrapT = THREE.ClampToEdgeWrapping;
    this.densityTex.generateMipmaps = false;
    this.densityTex.needsUpdate = true;

    this.noiseTex = makeNoiseTexture(NOISE_TEX_SIZE);

    const worldW = this.width  * TILE_SIZE;
    const worldH = this.height * TILE_SIZE;

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        tDensity:     { value: this.densityTex },
        tNoise:       { value: this.noiseTex },
        uTime:        { value: 0 },
        uMapSize:     { value: new THREE.Vector2(worldW, worldH) },
        uNoiseScaleA: { value: NOISE_WORLD_SCALE_A },
        uNoiseScaleB: { value: NOISE_WORLD_SCALE_B },
        uFogColor:    { value: new THREE.Color(0x06060c) },
        uFogHighlight:{ value: new THREE.Color(0x18181f) },
      },
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      vertexShader: /* glsl */`
        varying vec2 vWorldXZ;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldXZ = wp.xz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */`
        uniform sampler2D tDensity;
        uniform sampler2D tNoise;
        uniform float uTime;
        uniform vec2  uMapSize;
        uniform float uNoiseScaleA;
        uniform float uNoiseScaleB;
        uniform vec3  uFogColor;
        uniform vec3  uFogHighlight;
        varying vec2 vWorldXZ;

        void main() {
          vec2 densityUV = vWorldXZ / uMapSize;
          if (densityUV.x < 0.0 || densityUV.x > 1.0 ||
              densityUV.y < 0.0 || densityUV.y > 1.0) discard;

          float density = texture2D(tDensity, densityUV).r;
          if (density < 0.002) discard;

          // Two scrolling octaves of seamless value noise — creates a
          // cloudy, slowly-churning layer. Offsets are intentionally unequal
          // so the layers never line up.
          vec2 nuvA = vWorldXZ / uNoiseScaleA + vec2( uTime * 0.010,  uTime * 0.006);
          vec2 nuvB = vWorldXZ / uNoiseScaleB + vec2(-uTime * 0.014,  uTime * 0.009);
          float nA = texture2D(tNoise, nuvA).r;
          float nB = texture2D(tNoise, nuvB).r;
          float cloud = clamp(nA * 0.75 + nB * 0.45, 0.0, 1.0);

          // Color: dark base, lifted where cloud value is high — the cloud
          // drift now lives purely in the color channel so unexplored tiles
          // stay fully masked (no leaking the bright scene through alpha holes).
          vec3 color = mix(uFogColor, uFogHighlight, cloud);

          // Alpha = density. Fully unexplored tiles are fully opaque; only
          // the fade-in/out at exploration boundaries is translucent.
          gl_FragColor = vec4(color, clamp(density, 0.0, 1.0));
        }
      `,
    });

    const geo = new THREE.PlaneGeometry(worldW, worldH).rotateX(-Math.PI / 2);
    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.position.set(worldW / 2, FOG_HEIGHT, worldH / 2);
    // Render after opaque geometry and additive lights so alpha blends correctly.
    this.mesh.renderOrder = 100;
    this.group.add(this.mesh);
  }

  private isClearTile(x: number, y: number, exploration: ExplorationData): boolean {
    const tile = this.lab.tiles[y]?.[x];
    if (tile === Tile.Base || tile === Tile.Door) return true;
    return exploration.isRevealed(x, y);
  }

  update(dt: number, time: number, exploration: ExplorationData): void {
    const step = Math.max(1, Math.ceil(dt * FOG_FADE_SPEED * 255));
    const texArr = this.densityTex.image.data as Uint8Array;
    const w = this.width;
    let dirty = false;

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        const target = this.isClearTile(x, y, exploration) ? 0 : 255;
        const cur = this.density[i]!;
        if (cur === target) continue;
        const next = cur > target
          ? Math.max(target, cur - step)
          : Math.min(target, cur + step);
        this.density[i] = next;
        texArr[i * 4] = next;
        dirty = true;
      }
    }

    if (dirty) this.densityTex.needsUpdate = true;
    (this.material.uniforms.uTime as { value: number }).value = time;
  }

  dispose(): void {
    this.group.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.densityTex.dispose();
    this.noiseTex.dispose();
  }
}

/**
 * Seamless tileable value-noise texture with 3 octaves of fbm. Grid-wrap
 * arithmetic keeps the texture tileable at any integer multiple, so we can
 * freely scale it over the map without visible seams.
 */
function makeNoiseTexture(size: number): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  const data = img.data;

  const grid = 32;
  const rand = new Float32Array(grid * grid);
  for (let i = 0; i < rand.length; i++) rand[i] = Math.random();

  const smoothstep = (t: number): number => t * t * (3 - 2 * t);
  const wrap = (v: number): number => ((v % grid) + grid) % grid;
  const g = (gx: number, gy: number): number => rand[wrap(gy) * grid + wrap(gx)]!;

  const valueNoise = (x: number, y: number): number => {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const tx = smoothstep(x - x0);
    const ty = smoothstep(y - y0);
    const v00 = g(x0,     y0);
    const v10 = g(x0 + 1, y0);
    const v01 = g(x0,     y0 + 1);
    const v11 = g(x0 + 1, y0 + 1);
    const a = v00 * (1 - tx) + v10 * tx;
    const b = v01 * (1 - tx) + v11 * tx;
    return a * (1 - ty) + b * ty;
  };

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const fx = (x / size) * grid;
      const fy = (y / size) * grid;
      let v = 0;
      let amp = 0.55;
      let freq = 1;
      for (let o = 0; o < 3; o++) {
        v += valueNoise(fx * freq, fy * freq) * amp;
        amp *= 0.5;
        freq *= 2;
      }
      const byte = Math.max(0, Math.min(255, Math.floor(v * 255)));
      const p = (y * size + x) * 4;
      data[p]     = byte;
      data[p + 1] = byte;
      data[p + 2] = byte;
      data[p + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  return tex;
}
